import {httpClientV2} from "../../../../infrastructure/http";
import {
  CoverImage,
  Media,
  OfferingType,
  UploadImageParams,
  UploadContentParams,
  DeleteMediaParams,
  UploadResult,
  UploadProgress,
} from "../../domain";
import {CoverImageMapper, MediaMapperV2} from "../mappers/media.mapper.v2";
import {
  GetCoverImagesApiResponse,
  V2CoverImageUploadResponse,
  V2PortfolioUploadResponse,
} from "../mappers/media.mapper.types";
import {MediaRepository} from "./media.repository";

/**
 * MediaRepositoryV2
 *
 * Implementation of MediaRepository using v2 API endpoints.
 * v2 endpoints:
 * - POST /uploads/cover-image - Upload cover images for offerings
 * - DELETE /uploads/media/:mediaId - Delete media
 * - POST /uploads/product-service/portfolio-image - Upload portfolio images
 */
export class MediaRepositoryV2 implements MediaRepository {
  /**
   * Upload a single image for an offering using v2 endpoint
   * POST /uploads/cover-image
   */
  async uploadImage(params: UploadImageParams): Promise<UploadResult> {
    const formData = MediaMapperV2.toCoverImageFormData({
      file: params.file,
      offeringId: params.offeringId,
      offeringType: params.offeringType,
    });

    const response = await httpClientV2.post<V2CoverImageUploadResponse>(
      "uploads/cover-image",
      formData,
      {
        headers: {"Content-Type": "multipart/form-data"},
        onUploadProgress: params.options?.onProgress
          ? (event) => {
              if (event.total) {
                const progress: UploadProgress = {
                  loaded: event.loaded,
                  total: event.total,
                  percent: Math.round((event.loaded * 100) / event.total),
                };
                params.options?.onProgress?.(progress);
              }
            }
          : undefined,
        cancelToken: params.options?.cancelToken,
      }
    );

    return MediaMapperV2.toUploadResult(response, params.offeringType);
  }

  /**
   * Upload multiple images for an offering using v2 endpoint
   * POST /uploads/cover-image (multiple files)
   */
  async uploadImages(params: {
    files: File[];
    offeringId: string | number;
    offeringType: UploadImageParams["offeringType"];
    options?: UploadImageParams["options"];
  }): Promise<UploadResult> {
    const formData = MediaMapperV2.toCoverImageFormDataMultiple({
      files: params.files,
      offeringId: params.offeringId,
      offeringType: params.offeringType,
    });

    const response = await httpClientV2.post<V2CoverImageUploadResponse>(
      "uploads/cover-image",
      formData,
      {
        headers: {"Content-Type": "multipart/form-data"},
        onUploadProgress: params.options?.onProgress
          ? (event) => {
              if (event.total) {
                const progress: UploadProgress = {
                  loaded: event.loaded,
                  total: event.total,
                  percent: Math.round((event.loaded * 100) / event.total),
                };
                params.options?.onProgress?.(progress);
              }
            }
          : undefined,
        cancelToken: params.options?.cancelToken,
      }
    );

    return MediaMapperV2.toUploadResult(response, params.offeringType);
  }

  /**
   * Upload binary content (PDF, video, etc.) for a product
   * @throws Not implemented here — binary product content upload goes
   * through DigitalProductRepositoryV2.uploadContent instead.
   */
  async uploadContent(params: UploadContentParams): Promise<Media> {
    throw new Error(
      "MediaRepositoryV2.uploadContent not implemented. Use DigitalProductRepositoryV2.uploadContent for content uploads."
    );
  }

  /**
   * Delete a media item using v2 endpoint
   * DELETE /uploads/media/:mediaId
   */
  async deleteMedia(params: DeleteMediaParams): Promise<void> {
    await httpClientV2.delete(`uploads/media/${params.mediaId}`);
  }

  /**
   * Upload portfolio images (v2 specific)
   * POST /uploads/product-service/portfolio-image
   */
  async uploadPortfolioImages(params: {
    files: File[];
    productServiceId: string | number;
    options?: UploadImageParams["options"];
  }): Promise<UploadResult> {
    const formData = MediaMapperV2.toFormDataMultiple({
      files: params.files,
      productServiceId: params.productServiceId,
    });

    const response = await httpClientV2.post<V2PortfolioUploadResponse>(
      "uploads/product-service/portfolio-image",
      formData,
      {
        headers: {"Content-Type": "multipart/form-data"},
        onUploadProgress: params.options?.onProgress
          ? (event) => {
              if (event.total) {
                const progress: UploadProgress = {
                  loaded: event.loaded,
                  total: event.total,
                  percent: Math.round((event.loaded * 100) / event.total),
                };
                params.options?.onProgress?.(progress);
              }
            }
          : undefined,
        cancelToken: params.options?.cancelToken,
      }
    );

    return MediaMapperV2.toUploadResult(response, "portfolio");
  }

  /**
   * Get cover images for an offering
   * GET /uploads/cover-image?offeringType=X&offeringId=Y
   */
  async getCoverImages(
    offeringType: Exclude<OfferingType, "portfolio">,
    offeringId: string | number
  ): Promise<CoverImage[]> {
    const response = await httpClientV2.get<{data: GetCoverImagesApiResponse}>(
      "uploads/cover-image",
      {
        params: {
          offeringType: MediaMapperV2.toV2OfferingType(offeringType),
          offeringId,
        },
      }
    );

    return CoverImageMapper.toDomainList(response.data);
  }
}
